import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Alert,
  Chip,
  Stack,
  Autocomplete,
  CircularProgress,
} from '@mui/material';

const BASE_AUDIENCE_MODES = [
  { value: 'all', label: 'All employees' },
  { value: 'by_role', label: 'By role' },
  { value: 'by_department', label: 'By department' },
  { value: 'by_employee', label: 'Specific employees' },
  { value: 'advanced', label: 'Advanced rules' },
];

// Mobile-only mode: targets every registered device in app_devices, including
// app installs that have NOT logged in. Backend dispatcher branches on this
// (audience_mode === 'all_devices') and ignores audience_rules.
const MOBILE_EXTRA_MODES = [
  {
    value: 'all_devices',
    label: 'All app installs (incl. not logged in)',
    description:
      'Push to every device that registered the app (logged-in users + anonymous installs from app_devices). Employee filters do not apply.',
  },
];

export default function EmailCampaignAudienceStep({
  value,
  onChange,
  lookups,
  selectedEmployees,
  onSelectedEmployeesChange,
  employeeOptions,
  onEmployeeSearch,
  employeeLoading,
  estimatedRecipients,
  estimateLoading,
  readOnly = false,
  // Optional - when set to 'mobile', exposes the 'all_devices' audience mode.
  // For email campaigns leave undefined / 'email'.
  campaignType = 'email',
}) {
  const rules = value?.audience_rules || {};
  const AUDIENCE_MODES =
    campaignType === 'mobile' ? [...BASE_AUDIENCE_MODES, ...MOBILE_EXTRA_MODES] : BASE_AUDIENCE_MODES;

  const roles = Array.isArray(lookups?.roles) ? lookups.roles : [];
  const statuses = Array.isArray(lookups?.statuses?.employee)
    ? lookups.statuses.employee
    : ['active', 'on_leave', 'terminated'];

  const departmentOptions = (Array.isArray(lookups?.departments) ? lookups.departments : []).map((item) => ({
    id: item.id,
    label: item.name || `Department #${item.id}`,
  }));

  const updateRules = (patch) => {
    onChange({
      ...value,
      audience_rules: {
        ...rules,
        ...patch,
      },
    });
  };

  const setAudienceMode = (mode) => {
    onChange({
      ...value,
      audience_mode: mode,
      audience_rules: {
        ...rules,
      },
    });
  };

  return (
    <Stack spacing={2}>
      <FormControl>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Audience Mode
        </Typography>

        <RadioGroup
          value={value?.audience_mode || 'all'}
          onChange={(event) => setAudienceMode(event.target.value)}
        >
          {AUDIENCE_MODES.map((mode) => (
            <FormControlLabel
              key={mode.value}
              value={mode.value}
              control={<Radio />}
              label={mode.label}
              disabled={readOnly}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {value?.audience_mode === 'all_devices' ? (
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Push to every registered device
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Reaches all app installs that registered an Expo token, including users who
            have not logged in yet. Employee filters (role / department / specific employees)
            are ignored for this mode. Anonymous devices receive a push only - no inbox entry.
          </Typography>
        </Alert>
      ) : null}

      {value?.audience_mode === 'by_role' ? (
        <Autocomplete
          multiple
          options={roles}
          value={Array.isArray(rules.roles) ? rules.roles : []}
          onChange={(_, next) => updateRules({ roles: next })}
          disableCloseOnSelect
          disabled={readOnly}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip {...getTagProps({ index })} key={`${option}-${index}`} size="small" label={option} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Roles" placeholder="Select roles" />}
        />
      ) : null}

      {value?.audience_mode === 'by_department' ? (
        <Autocomplete
          multiple
          options={departmentOptions}
          value={departmentOptions.filter((item) => (rules.department_ids || []).includes(item.id))}
          onChange={(_, next) => updateRules({ department_ids: next.map((item) => item.id) })}
          disableCloseOnSelect
          disabled={readOnly}
          getOptionLabel={(option) => option.label}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip {...getTagProps({ index })} key={`${option.id}-${index}`} size="small" label={option.label} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Departments" placeholder="Select departments" />}
        />
      ) : null}

      {value?.audience_mode === 'by_employee' ? (
        <Autocomplete
          multiple
          openOnFocus
          options={employeeOptions}
          loading={employeeLoading}
          filterOptions={(x) => x}
          value={selectedEmployees}
          onChange={(_, next) => {
            onSelectedEmployeesChange(next);
            updateRules({ employee_ids: next.map((item) => item.id) });
          }}
          onInputChange={(_, inputValue, reason) => onEmployeeSearch?.(inputValue, reason)}
          disabled={readOnly}
          getOptionLabel={(option) => `${option.name || 'Unknown'} (${option.email || option.employee_code || option.id})`}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          noOptionsText={employeeLoading ? 'Searching employees...' : 'No employees found'}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employees"
              placeholder="Search by name, email, employee code"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {employeeLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      ) : null}

      {value?.audience_mode === 'advanced' ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Logic"
              value={rules.logic || 'and'}
              onChange={(event) => updateRules({ logic: event.target.value })}
              disabled={readOnly}
            >
              <MenuItem value="and">AND</MenuItem>
              <MenuItem value="or">OR</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={8}>
            <Autocomplete
              multiple
              options={roles}
              value={Array.isArray(rules.roles) ? rules.roles : []}
              onChange={(_, next) => updateRules({ roles: next })}
              disableCloseOnSelect
              disabled={readOnly}
              renderInput={(params) => <TextField {...params} label="Roles" placeholder="Any roles" />}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={departmentOptions}
              value={departmentOptions.filter((item) => (rules.department_ids || []).includes(item.id))}
              onChange={(_, next) => updateRules({ department_ids: next.map((item) => item.id) })}
              disableCloseOnSelect
              disabled={readOnly}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => <TextField {...params} label="Departments" placeholder="Any departments" />}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              options={statuses}
              value={Array.isArray(rules.statuses) ? rules.statuses : []}
              onChange={(_, next) => updateRules({ statuses: next })}
              disableCloseOnSelect
              disabled={readOnly}
              renderInput={(params) => <TextField {...params} label="Statuses" placeholder="Any statuses" />}
            />
          </Grid>
        </Grid>
      ) : null}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Estimated recipients:
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {estimateLoading ? 'Calculating...' : Number(estimatedRecipients || 0)}
        </Typography>
      </Box>

      {!estimateLoading && Number(estimatedRecipients || 0) === 0 ? (
        <Alert severity="warning">No recipients match the selected audience rules.</Alert>
      ) : null}
    </Stack>
  );
}
